export interface Discount {
  code: string | null;
  percent?: number | null;
  fixedPrice?: number | null;
  validUntil?: string | null;
  validUntilRemark?: string | null;
  firstPurchaseOnly?: boolean;
  phases?: number | number[] | null;
  planType?: string | null;
  accountSize?: number | null;
}

export interface Challenge {
  phases?: number | string;
  accountSize?: number;
  planType?: string;
  fee?: number;
  type?: string;
  label?: string;
  maxDrawdownPercent?: number;
  maxDailyDrawdownPercent?: number;
  payoutDelayDays?: number;
  [key: string]: unknown; // Replace `any` with `unknown`
}

export function getBestDiscount(
  discountList: Discount[],
  challenge: Challenge | undefined
) {
  if (!Array.isArray(discountList) || !challenge) return null;

  const now = Date.now();

  const validDiscounts = discountList
    .filter(d => d.code && (d.percent !== undefined || d.fixedPrice !== undefined)) // Ensure valid discount
    .filter(d => {
      // Check expiration
      const isNotExpired = !d.validUntil || new Date(d.validUntil).getTime() > now;

      // Check phase match
      const isPhaseMatch = d.phases === undefined || d.phases === null ||
        (Array.isArray(d.phases) ? d.phases.includes(challenge.phases as number) : d.phases === challenge.phases);

      // Combine all conditions
      return isNotExpired && isPhaseMatch;
    });

  // Sort by savings amount (either percent or fixed price)
  return validDiscounts.sort((a, b) => {
    if (!challenge.fee) return 0;

    // If both have fixed prices, compare the fixed prices
    if (a.fixedPrice !== null && b.fixedPrice !== null) {
      return (a.fixedPrice || 0) - (b.fixedPrice || 0);
    }

    // If both have percentages, compare the percentages
    if (a.percent !== null && b.percent !== null) {
      return (b.percent || 0) - (a.percent || 0);
    }

    // If mixed, calculate and compare actual savings
    const aValue = a.fixedPrice !== null
      ? a.fixedPrice
      : challenge.fee * (1 - (a.percent || 0) / 100);
    const bValue = b.fixedPrice !== null
      ? b.fixedPrice
      : challenge.fee * (1 - (b.percent || 0) / 100);

    return (aValue || 0) - (bValue || 0);
  })[0] || null;
}
